import { useEffect } from 'react';
import PanelWidthToggle from '../lecture/components/PanelWidthToggle';
import { MessagesSquare, Radio, Users, X } from 'lucide-react';
import Card from '../../components/ui/Card';
import { useFeature } from '../../stores/featureStore';
import { useClassroomStore } from './store';
import type { ActivityStep, ClassActivity, StepMode } from './classroomApi';
import LiveQuestion from './components/LiveQuestion';
import ResponseControl from './components/ResponseControl';
import GroupChip from './components/GroupChip';
import { deriveRevoteOf } from './runSheet/livePhase';

// BP 22 classroom feature — public surface. `LiveActivity` (the /class page
// card) and `ClassPanel` (the lecture-player's Class side panel, BP 22 §
// "Lecture player panel") render the SAME live-step body — the store's
// ref-counted 5 s poll of GET /class/live (store.ts) is shared by both, so
// having the card and the panel open at once still costs one interval.
// Neither component renders a widget of its own: `StepPrompt` hands
// `widget_refs` to the existing `WidgetRenderer` (BP 09 catalog).
//
// WIP-018 (build order §2.3) — `ResponseControl` moved to its own file
// (components/ResponseControl.tsx) so the rest of the live-activity set can
// extend it without editing this one again.

/** instruct/group steps have nothing to submit. Saying so is not decoration:
 *  136 of the catalogue's 422 steps are group steps, and a question with no
 *  control under it otherwise reads as "this page is still loading" or "my
 *  phone didn't get the options". */
function NothingToSubmit({ mode }: { mode: StepMode }) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-ws-border bg-ws-surface-2 px-4 py-3">
      <MessagesSquare size={20} className="mt-0.5 shrink-0 text-ws-ink-3" aria-hidden="true" />
      <p className="text-sm text-ws-ink-2">
        {mode === 'group'
          ? 'Talk this through with your group — nothing to submit for this step.'
          : 'Your instructor is walking through this — nothing to submit for this step.'}
      </p>
    </div>
  );
}

function LiveStep({ activity }: { activity: ClassActivity }) {
  const hasResponded = useClassroomStore((s) => s.hasResponded);
  const sessionId = useClassroomStore((s) => s.session?.id);
  const step = activity.live_step != null ? activity.steps[activity.live_step] : undefined;

  if (!step) {
    return <p className="text-base text-ws-ink-2">Waiting for the instructor…</p>;
  }

  // instruct/group steps are the instructor talking or a group discussing —
  // nothing for the student to submit; poll/response steps get a control.
  const showControl = step.mode === 'poll' || step.mode === 'response';

  // design §4.3 — a revote step shows the ORIGINAL question, not the
  // authored "Discuss, then vote again." placeholder, and never the
  // student's round-1 pick or any count. `revote_of` is resolved
  // client-side against this same activity's steps.
  const revoteOf = deriveRevoteOf(step, activity.steps);
  const originalStep = revoteOf ? activity.steps.find((s) => s.key === revoteOf) : undefined;
  const displayStep: ActivityStep = originalStep
    ? { ...step, prompt: originalStep.prompt, options: originalStep.options }
    : step;

  // /class live redesign (2026-09-11) — everything that is not the question
  // is one wrapping meta line above it. The old card spent four lines before
  // the prompt (card title, session label, step label, then a text-sm
  // prompt); on a 390 px phone that is most of what a student saw when a new
  // step went live.
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {/* WIP-007 (docs/AUDIT-ACCESSIBILITY.md #1): bg-ws-danger is a TEXT
            token and is only ~3.9:1 light / 3.4:1 dark under white — sub-AA.
            bg-ws-danger-solid + text-ws-on-danger is the fill+text pair. */}
        <span className="inline-flex items-center gap-1 rounded-md bg-ws-danger-solid px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ws-on-danger">
          <Radio size={9} className="animate-pulse" aria-hidden="true" /> Live now
        </span>
        <p className="text-[11px] font-medium uppercase tracking-wide text-ws-ink-3">{step.label}</p>
        {/* ── live-activity slots (design §4.3/§6, build order §2.5) ── */}
        {sessionId && <GroupChip sessionId={sessionId} />}
        {revoteOf && (
          <span className="inline-flex items-center rounded-md bg-ws-accent-quiet px-2 py-0.5 text-[11px] font-semibold text-ws-accent-ink">
            Second vote
          </span>
        )}
      </div>

      <LiveQuestion step={displayStep} widgetRefs={activity.widget_refs}>
        {showControl ? (
          // Keyed by step: without it React reuses the same control instance
          // across a step change, and the previous question's highlighted
          // option (or half-typed answer) is still sitting there under the
          // NEW question — the component's local `picked`/input state has no
          // idea the question moved.
          <ResponseControl
            key={step.key}
            activityId={activity.id}
            step={displayStep}
            submitted={hasResponded(step.key)}
          />
        ) : null}
      </LiveQuestion>

      {!showControl && <NothingToSubmit mode={step.mode} />}
    </div>
  );
}

// Shared "what's live right now" body for both surfaces below. A live class
// session with nothing currently live (between activities, or before the
// first one starts) shows the waiting state rather than nothing, per BP 22.
function LiveBody() {
  const activity = useClassroomStore((s) => s.activity);
  if (!activity) {
    return <p className="text-base text-ws-ink-2">Waiting for the instructor…</p>;
  }
  return <LiveStep activity={activity} />;
}

/** The current session's live activity, if any. Reads its own state (live
 * poll) — takes no props. Renders nothing when no class session is live. */
export function LiveActivity() {
  const enabled = useFeature('class_activities');
  const session = useClassroomStore((s) => s.session);
  const startWatching = useClassroomStore((s) => s.startWatching);
  const stopWatching = useClassroomStore((s) => s.stopWatching);

  useEffect(() => {
    if (!enabled) return undefined;
    startWatching();
    return () => stopWatching();
  }, [enabled, startWatching, stopWatching]);

  if (!enabled || !session) return null;

  // The card header this used to carry ("Class activity" + the session label
  // + an icon) is gone on purpose: the page header, the status banner and
  // the check-in card above it already say which class this is, and on a
  // phone those three lines were pushing the question itself under the fold.
  // The question's own <h2> (LiveQuestion) is the card's heading now.
  return (
    <Card className="p-4 sm:p-5">
      {/* WIP-007 (docs/AUDIT-ACCESSIBILITY.md #3) — an activity going live, or
          the instructor advancing to the next step, is a content change the
          5s poll makes with nobody clicking anything. Without aria-live, a
          screen-reader student only finds out by re-reading the page. */}
      <div aria-live="polite" aria-atomic="true">
        <LiveBody />
      </div>
    </Card>
  );
}

interface ClassPanelProps {
  lessonId: string;
  onClose: () => void;
  /** Panel width, owned by LecturePlayer — the toggle lives in this header
   *  beside Close rather than in a strip of its own. */
  wide?: boolean;
  onToggleWide?: () => void;
}

/** The lecture-player "Class" panel (WS-C's PanelMode === 'class' branch).
 * Same live content as `LiveActivity`, in the right-rail panel chrome shared
 * with Explain / Ask / Tutor (header + close button, scrolling body). The
 * panel is only reachable while the store already reports a live session for
 * `lessonId` (LecturePlayer's header-button condition), so it renders from
 * the shared store state rather than re-filtering by lessonId itself. */
export function ClassPanel({ onClose, wide, onToggleWide }: ClassPanelProps) {
  const session = useClassroomStore((s) => s.session);
  const startWatching = useClassroomStore((s) => s.startWatching);
  const stopWatching = useClassroomStore((s) => s.stopWatching);

  useEffect(() => {
    startWatching();
    return () => stopWatching();
  }, [startWatching, stopWatching]);

  return (
    <div className="flex h-full flex-col bg-ws-surface">
      <header className="flex items-center justify-between border-b border-ws-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Users size={15} className="text-ws-accent" />
          <h2 className="text-sm font-semibold text-ws-ink">{session?.label || 'Class'}</h2>
        </div>
        {onToggleWide && <PanelWidthToggle wide={!!wide} onToggle={onToggleWide} />}
          <button
          type="button"
          onClick={onClose}
          className="grid size-9 sm:size-7 place-items-center rounded-md text-ws-ink-3 transition-colors hover:bg-ws-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
          title="Close"
          aria-label="Close class panel"
        >
          <X size={15} />
        </button>
      </header>
      {/* WIP-007 — see the matching comment in LiveActivity above. */}
      <div className="flex-1 overflow-y-auto px-4 py-4" aria-live="polite" aria-atomic="true">
        <LiveBody />
      </div>
    </div>
  );
}
